const API_BASE = '/api';

const TOKEN_KEY = 'mts-token';

export const setToken = (t) => {
  if (t) sessionStorage.setItem(TOKEN_KEY, t);
};
export const clearToken = () => sessionStorage.removeItem(TOKEN_KEY);
export const getToken = () => sessionStorage.getItem(TOKEN_KEY);

// Central fetch wrapper: attaches the auth token on every request and, on a 401
// (missing/expired/invalid token), clears the session and bounces to login.
const apiFetch = async (url, options = {}) => {
  const token = getToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem('mts-user');
    // Reload so App.js re-reads sessionStorage (now empty) and shows the login page.
    if (typeof window !== 'undefined') window.location.reload();
    throw new Error('Session expired. Please log in again.');
  }
  return response;
};

export const fetchGPSData = async () => {
  const response = await apiFetch(`${API_BASE}/gps-data`);
  if (!response.ok) throw new Error('Failed to fetch GPS data');
  return response.json();
};

export const refreshGPSData = async () => {
  const response = await apiFetch(`${API_BASE}/refresh`);
  if (!response.ok) throw new Error('Failed to refresh GPS data');
  return response.json();
};

export const fetchHistory = async (params = {}) => {
  const { gps = '', days = 7, limit = 1000 } = params;
  let url = `${API_BASE}/history?days=${days}&limit=${limit}`;
  if (gps) url += `&gps=${gps}`;

  const response = await apiFetch(url);
  if (!response.ok) throw new Error('Failed to fetch history');
  return response.json();
};

export const fetchDevices = async () => {
  const response = await apiFetch(`${API_BASE}/devices`);
  if (!response.ok) throw new Error('Failed to fetch devices');
  return response.json();
};

export const fetchStats = async () => {
  const response = await apiFetch(`${API_BASE}/stats`);
  if (!response.ok) throw new Error('Failed to fetch stats');
  return response.json();
};

export const exportCSV = async (params = {}) => {
  const { gps = '', days = 30 } = params;
  let url = `${API_BASE}/export?days=${days}`;
  if (gps) url += `&gps=${gps}`;
  // Fetch with the auth token (a plain window.location navigation can't send the
  // Authorization header) and trigger a download from the returned blob.
  const response = await apiFetch(url);
  if (!response.ok) throw new Error('Failed to export CSV');
  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = `gps-history-${days}d${gps ? `-${gps}` : ''}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
};

export const login = async (username, password) => {
  // Plain fetch (no wrapper): a 401 here means bad credentials, which the login
  // form should display — not a session-expiry bounce.
  const response = await fetch(`${API_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Login failed');
  if (data.token) setToken(data.token);
  return data;
};

export const updateUserPreferences = async (userId, preferences) => {
  const response = await apiFetch(`${API_BASE}/user/preferences`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, ...preferences }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Failed to save preferences');
  return data;
};

export const fetchUsers = async () => {
  const response = await apiFetch(`${API_BASE}/users`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Failed to fetch users');
  return data.users || [];
};

export const updateUser = async (userId, fields) => {
  const response = await apiFetch(`${API_BASE}/users/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Failed to update user');
  return data;
};

export const resetUserPassword = async (userId) => {
  const response = await apiFetch(`${API_BASE}/users/${userId}/reset-password`, { method: 'POST' });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Failed to reset password');
  return data;
};

export const createUser = async ({ username, fullName, email, password, role, country, businessUnit, region }) => {
  const response = await apiFetch(`${API_BASE}/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, fullName, email, password, role, country, businessUnit, region }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Failed to create user');
  return data;
};

export const fetchOmniboxes = async () => {
  const response = await apiFetch(`${API_BASE}/omnibox`);
  if (!response.ok) throw new Error('Failed to fetch omniboxes');
  return response.json();
};

export const fetchOmnibox = async (id) => {
  const response = await apiFetch(`${API_BASE}/omnibox/${id}`);
  if (!response.ok) throw new Error('Failed to fetch omnibox');
  return response.json();
};

export const fetchGpsSystems = async () => {
  const response = await apiFetch(`${API_BASE}/gps-systems`);
  if (!response.ok) throw new Error('Failed to fetch GPS systems');
  return response.json();
};

export const createGpsSystem = async (data) => {
  const response = await apiFetch(`${API_BASE}/gps-systems`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to create GPS system');
  return json;
};

export const updateGpsSystem = async (gpsNumber, data) => {
  const response = await apiFetch(`${API_BASE}/gps-systems/${gpsNumber}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to update GPS system');
  return json;
};

export const deleteGpsSystem = async (gpsNumber) => {
  const response = await apiFetch(`${API_BASE}/gps-systems/${gpsNumber}`, { method: 'DELETE' });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to delete GPS system');
  return json;
};

export const fetchSurveyGps = async () => {
  const response = await apiFetch('/api/survey-gps');
  if (!response.ok) throw new Error('Failed to fetch survey GPS');
  return response.json();
};

// Fresh Samsara location for a single tracker serial (bypasses the list's cache).
export const fetchTrackerLocation = async (serial) => {
  const response = await apiFetch(`/api/survey-gps/tracker-location?serial=${encodeURIComponent(serial)}`);
  if (!response.ok) throw new Error('Failed to fetch tracker location');
  return response.json();
};

// The rover, test box or data logger already holding a tracker serial (or null).
// kind/id name the unit being edited, which is left out of the search.
export const fetchTrackerOwner = async (serial, kind, id) => {
  const params = new URLSearchParams({ serial, kind });
  if (id != null) params.set('id', id);
  const response = await apiFetch(`/api/trackers/owner?${params}`);
  if (!response.ok) throw new Error('Failed to look up tracker');
  return response.json();
};

// The NTRIP license a rover is signed out on ({ ntrip: null } when none).
export const fetchRoverNtrip = async (roverId) => {
  const response = await apiFetch(`/api/survey-gps/${roverId}/ntrip`);
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to fetch NTRIP details');
  return json;
};

// Move a rover's / GPS system's license to the other NTRIP provider.
export const switchRoverProvider = async (roverId) => {
  const response = await apiFetch(`/api/survey-gps/${roverId}/switch-provider`, { method: 'POST' });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to switch provider');
  return json;
};

export const switchGpsSystemProvider = async (gpsNumber, fullName) => {
  const response = await apiFetch(`${API_BASE}/gps-systems/${gpsNumber}/switch-provider`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fullName }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to switch provider');
  return json;
};

export const createSurveyGps = async (data) => {
  const response = await apiFetch('/api/survey-gps', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to create rover');
  return json;
};

export const updateSurveyGps = async (id, data) => {
  const response = await apiFetch(`/api/survey-gps/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to update rover');
  return json;
};

export const deleteSurveyGps = async (id) => {
  const response = await apiFetch(`/api/survey-gps/${id}`, { method: 'DELETE' });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to delete rover');
  return json;
};

export const assignLicenseToRover = async (roverId, licenseNumber) => {
  const response = await apiFetch(`/api/survey-gps/${roverId}/license`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ license_number: licenseNumber }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to assign license to rover');
  return json;
};

export const createLoadTestBox = async (data) => {
  const response = await apiFetch('/api/load-test-boxes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to create test box');
  return json;
};

export const updateLoadTestBox = async (id, data) => {
  const response = await apiFetch(`/api/load-test-boxes/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to update test box');
  return json;
};

export const deleteLoadTestBox = async (id) => {
  const response = await apiFetch(`/api/load-test-boxes/${id}`, { method: 'DELETE' });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to delete test box');
  return json;
};

// Attach (loggerId) or detach (null) a data logger — used by both the dialog
// dropdowns and drag-and-drop on the Data Loggers page.
export const attachLoggerToBox = async (boxId, loggerId) => {
  const response = await apiFetch(`/api/load-test-boxes/${boxId}/logger`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ logger_id: loggerId ?? null }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to attach logger');
  return json;
};

export const fetchDataLoggers = async () => {
  const response = await apiFetch('/api/data-loggers');
  if (!response.ok) throw new Error('Failed to fetch data loggers');
  return response.json();
};

export const createDataLogger = async (data) => {
  const response = await apiFetch('/api/data-loggers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to create data logger');
  return json;
};

export const updateDataLogger = async (id, data) => {
  const response = await apiFetch(`/api/data-loggers/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to update data logger');
  return json;
};

export const deleteDataLogger = async (id) => {
  const response = await apiFetch(`/api/data-loggers/${id}`, { method: 'DELETE' });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to delete data logger');
  return json;
};

// Issues (or replaces) the key a logger's CR6 program sends with each HTTPPost.
export const generateLoggerIngestKey = async (id) => {
  const response = await apiFetch(`/api/data-loggers/${id}/ingest-key`, { method: 'POST' });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to generate ingest key');
  return json;
};

// Reads back a logger's current ingest key (admin only).
export const fetchLoggerIngestKey = async (id) => {
  const response = await apiFetch(`/api/data-loggers/${id}/ingest-key`);
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to read ingest key');
  return json;
};

export const bulkAssignLicenseNumbers = async () => {
  const response = await apiFetch(`/api/licenses/bulk-assign-numbers`, { method: 'POST' });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to assign numbers');
  return json;
};

export const fetchAvailableCount = async () => {
  const response = await apiFetch('/api/licenses/available-count');
  if (!response.ok) return { available_count: 0, rtk_available_count: 0 };
  return response.json();
};

export const fetchNextLicenseNumber = async (region = '') => {
  const response = await apiFetch(`/api/licenses/next-number?region=${encodeURIComponent(region)}`);
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to generate number');
  return json.number;
};

export const updateLicenseName = async (licenseId, menardInternalNumber) => {
  const response = await apiFetch(`/api/licenses/${licenseId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ menard_internal_number: menardInternalNumber }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to update license');
  return json;
};

export const assignLicenseToGps = async (gpsNumber, licenseId) => {
  const response = await apiFetch(`/api/gps-systems/${gpsNumber}/license`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ license_id: licenseId }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to assign license');
  return json;
};

export const fetchRegions = async (businessUnit, country) => {
  const params = new URLSearchParams();
  if (businessUnit) params.set('businessUnit', businessUnit);
  if (country) params.set('country', country);
  const qs = params.toString();
  const response = await apiFetch(`${API_BASE}/regions${qs ? `?${qs}` : ''}`);
  if (!response.ok) throw new Error('Failed to fetch regions');
  return response.json();
};

export const fetchBusinessUnitForRegion = async (region, country) => {
  const params = new URLSearchParams({ region });
  if (country) params.set('country', country);
  const response = await apiFetch(`${API_BASE}/business-unit-for-region?${params.toString()}`);
  if (!response.ok) throw new Error('Failed to fetch business unit');
  return response.json();
};

export const fetchBusinessUnits = async (country) => {
  const url = country ? `${API_BASE}/business-units?country=${encodeURIComponent(country)}` : `${API_BASE}/business-units`;
  const response = await apiFetch(url);
  if (!response.ok) throw new Error('Failed to fetch business units');
  return response.json();
};

export const fetchOffices = async () => {
  const response = await apiFetch(`${API_BASE}/offices`);
  if (!response.ok) throw new Error('Failed to fetch offices');
  return response.json();
};

export const fetchLicenseCheckouts = async () => {
  const response = await apiFetch(`${API_BASE}/licenses/checkouts`);
  if (!response.ok) throw new Error('Failed to fetch checkouts');
  return response.json();
};

export const checkoutLicense = async (data) => {
  const response = await apiFetch(`${API_BASE}/licenses/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Checkout failed');
  return json;
};

export const locationCheck = async (query) => {
  const response = await apiFetch(`${API_BASE}/location-check`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Location check failed');
  return json;
};

export const fetchCoverage = async () => {
  const response = await apiFetch(`${API_BASE}/coverage`);
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to fetch coverage');
  return json;
};

export const dismissCheckout = async (id) => {
  const response = await apiFetch(`${API_BASE}/licenses/checkouts/${id}/dismiss`, { method: 'POST' });
  if (!response.ok) throw new Error('Failed to dismiss');
  return response.json();
};

export const returnLicenseCheckout = async (id) => {
  const response = await apiFetch(`${API_BASE}/licenses/checkout/${id}/return`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Return failed');
  return json;
};

export const fetchLicenseBilling = async () => {
  const response = await apiFetch(`${API_BASE}/licenses/billing`);
  if (!response.ok) throw new Error('Failed to fetch billing');
  return response.json();
};

export const fetchLicenseHistory = async () => {
  const response = await apiFetch(`${API_BASE}/licenses/history`);
  if (!response.ok) throw new Error('Failed to fetch license history');
  return response.json();
};

export const extendLicenseCheckout = async (id, expiresAt) => {
  const response = await apiFetch(`${API_BASE}/licenses/checkout/${id}/extend`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expiresAt }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Extend failed');
  return json;
};

export const fetchLicenses = async (search = '') => {
  let url = `${API_BASE}/licenses`;
  if (search) url += `?search=${encodeURIComponent(search)}`;
  const response = await apiFetch(url);
  if (!response.ok) throw new Error('Failed to fetch licenses');
  return response.json();
};

export const provisionPointOne = async (data) => {
  const response = await apiFetch(`${API_BASE}/pointone/provision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Provision failed');
  return json;
};

export const provisionRtkPremium = async (data) => {
  const response = await apiFetch(`${API_BASE}/rtkpremium/provision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Provision failed');
  return json;
};

export const fetchRtkPremiumLicenses = async () => {
  const response = await apiFetch(`${API_BASE}/rtkpremium/licenses`);
  if (!response.ok) throw new Error('Failed to fetch Premium RTK licenses');
  return response.json();
};

export const decommissionPointOneLicense = async (id) => {
  const response = await apiFetch(`${API_BASE}/pointone/licenses/${id}/decommission`, { method: 'POST' });
  if (!response.ok) throw new Error('Failed to decommission license');
  return response.json();
};

export const decommissionRtkLicense = async (id) => {
  const response = await apiFetch(`${API_BASE}/rtkpremium/licenses/${id}/decommission`, { method: 'POST' });
  if (!response.ok) throw new Error('Failed to decommission license');
  return response.json();
};

export const updateRtkPremiumLabel = async (id, menardInternalNumber) => {
  const response = await apiFetch(`${API_BASE}/rtkpremium/licenses/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ menardInternalNumber }),
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Update failed');
  return json;
};

export const fetchProjects = async () => {
  const response = await apiFetch(`${API_BASE}/projects`);
  if (!response.ok) throw new Error('Failed to fetch projects');
  return response.json();
}

export const fetchTechniques = async () => {
  const response = await apiFetch(`${API_BASE}/techniques`);
  if (!response.ok) throw new Error('Failed to fetch techniques');
  return response.json();
}

export const fetchLoggers = async () => {
  const response = await apiFetch(`${API_BASE}/loggers`);
  if (!response.ok) throw new Error('Failed to fetch loggers');
  return response.json();
}

export const fetchCalibrations = async () => {
  const response = await apiFetch(`${API_BASE}/calibrations`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch calibrations');
  return data; // { optionsData, testData }
}

export const fetchLoadTestBoxes = async () => {
  const response = await apiFetch(`${API_BASE}/load-test-boxes`);
  if (!response.ok) throw new Error('Failed to fetch load test boxes');
  return response.json();
}

export const saveBoxProject = async (boxId, projectNumber) => {
  const response = await apiFetch(`${API_BASE}/load-test-boxes/${boxId}/project`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectNumber }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to save project');
  return data;
}

export const fetchBoxSensors = async (boxId) => {
  const response = await apiFetch(`${API_BASE}/load-test-boxes/${boxId}/sensors`);
  if (!response.ok) throw new Error('Failed to fetch box sensors');
  return response.json(); // { 'Load Cell': [...], 'LVDT Sensors': [...], ... }
}

export const saveBoxSensors = async (boxId, sensors) => {
  const response = await apiFetch(`${API_BASE}/load-test-boxes/${boxId}/sensors`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sensors }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to save sensor assignments');
  return data;
}

export const saveCalibrations = async (sensors) => {
  const response = await apiFetch(`${API_BASE}/calibrations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sensors }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to save calibrations');
  return data;
}

export const updateLoggers = async (loggerInfo) => {
  const response = await apiFetch(`${API_BASE}/update-logger`,{
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(loggerInfo)
  });
  if (!response.ok) throw new Error('Failed to update logger');
  return response.json();
}

export const fetchLoadTest = async (ltBoxId) => {
  const response = await apiFetch(`${API_BASE}/get-lt-data?lt_box_id=${ltBoxId}`);
  const data = await response.json()
  if (!response.ok) {
    const err = new Error(data.message);
    err.code = data.error;
    err.status = data.status;
    throw err;
  }
  return data;
}

// Calibration revisions of one load test (the `test.id` in the get-lt-data
// payload). Saving, applying and previewing each return dashboard data in the
// get-lt-data shape, recomputed with the calibration in question.
const calibrationRequest = async (path, options, fallback) => {
  const response = await apiFetch(`${API_BASE}/load-tests/${path}`, options);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || fallback);
  return data;
}

const jsonBody = (method, body) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

// Every recorded load test, newest first, for the Historical Data list:
// { tests: [{ id, start_time, end_time, duration_min, status, job_number,
// point_number, test_number, project_number, project_name, location,
// peak_kips, readings, logger_id, logger_name, box_id, box_name, rev_number,
// revision_count }] }.
export const fetchLoadTests = async () => {
  const response = await apiFetch(`${API_BASE}/load-tests`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || 'Failed to load the past tests');
  return data;
};

// Set a test's job by hand: any of { project_number (full, e.g. 952100-00, or
// '' to clear), point_number, test_number }. Only the fields sent change.
// Resolves to { test } in the History list's row shape.
export const setLoadTestJob = (testId, job) =>
  calibrationRequest(`${testId}/job`, jsonBody('PUT', job), 'Failed to save the job');

export const fetchCalibrationRevisions = (testId) =>
  calibrationRequest(`${testId}/calibration-revisions`, undefined, 'Failed to load calibration revisions');

// What the sensors assigned to the test's box are calibrated to today (their
// records under Equipment > Load Testing), as { box_id, settings }.
export const fetchSensorRecords = (testId) =>
  calibrationRequest(`${testId}/sensor-records`, undefined, 'Failed to load the sensor records');

// `view` is 'test' on a past test opened from Historical Data: the data sent
// back is then that test's, not the box's live view.
const viewQuery = (view) => (view ? `?view=${encodeURIComponent(view)}` : '');

export const saveCalibrationRevision = (testId, settings, note, view) =>
  calibrationRequest(`${testId}/calibration-revisions${viewQuery(view)}`, jsonBody('POST', { settings, note }), 'Failed to save the revision');

export const applyCalibrationRevision = (testId, revisionId, view) =>
  calibrationRequest(`${testId}/active-revision${viewQuery(view)}`, jsonBody('PUT', { revision_id: revisionId }), 'Failed to apply the revision');

export const previewCalibration = (testId, settings, view) =>
  calibrationRequest(`${testId}/calibration-preview${viewQuery(view)}`, jsonBody('POST', { settings }), 'Failed to preview the calibration');

// One recorded test as the History list shows it: { test }.
export const fetchLoadTestRecord = (testId) =>
  calibrationRequest(`${testId}`, undefined, 'Failed to load the test');

// A recorded test's dashboard data, converted with one of its revisions (the
// one in use when revisionId is left out). Same shape as fetchLoadTest, plus
// `viewing`: the revision shown and the one in use.
export const fetchPastTestData = async (testId, revisionId) => {
  const query = revisionId ? `?revision_id=${encodeURIComponent(revisionId)}` : '';
  const response = await apiFetch(`${API_BASE}/load-tests/${testId}/data${query}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || 'Failed to load the test');
    err.status = response.status;
    throw err;
  }
  return data;
};

// Download a recorded test as an Excel workbook (summary, steps, raw readings
// and converted readings), converted with revisionId (default: the revision
// in use). Fetched with the auth token and saved from the blob, under the
// name the server gives it.
export const downloadLoadTestExcel = async (testId, revisionId) => {
  const query = revisionId ? `?revision_id=${encodeURIComponent(revisionId)}` : '';
  const response = await apiFetch(`${API_BASE}/load-tests/${testId}/export${query}`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to export the test');
  }
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = match ? match[1] : `LoadTest_${testId}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
};
